import React from "react";
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Stack,
} from "@mui/material";
import {
  FitnessCenter as FitnessCenterIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Header component for training plan displaying title, description, and key info
 */
const TrainingPlanHeader = ({
  planData,
  organizationName,
  brandingColors,
  totalWorkouts,
}) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
        return "error";
      default:
        return "default";
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      strength: <FitnessCenterIcon />,
      cardio: <ScheduleIcon />,
      mixed: <TrendingUpIcon />,
      default: <AssignmentIcon />,
    };
    return icons[type?.toLowerCase()] || icons.default;
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Main Title Section */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography
          variant="h1"
          component="h1"
          gutterBottom
          sx={{
            color: brandingColors?.primary || "primary.main",
            mb: 1,
          }}
        >
          {planData.name}
        </Typography>

        <Typography
          variant="h5"
          component="h2"
          color="text.secondary"
          gutterBottom
        >
          {organizationName}
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
        >
          {planData.difficulty && (
            <Chip
              label={planData.difficulty}
              color={getDifficultyColor(planData.difficulty)}
              variant="filled"
              size="medium"
            />
          )}

          {planData.type && (
            <Chip
              icon={getTypeIcon(planData.type)}
              label={`${planData.type} Training`}
              color="primary"
              variant="outlined"
              size="medium"
            />
          )}

          <Chip
            icon={<ScheduleIcon />}
            label={`${totalWorkouts} Days`}
            color="secondary"
            variant="outlined"
            size="medium"
          />
        </Stack>
      </Box>

      {/* Plan Overview Card */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Description */}
            <Grid
              item
              xs={12}
              md={planData.equipment_needed?.length > 0 ? 8 : 12}
            >
              <Typography variant="h6" component="h3" gutterBottom>
                Plan Overview
              </Typography>
              <Box sx={{ "& p": { mb: 2 }, "& ul, & ol": { pl: 3, mb: 2 } }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {planData.description ||
                    `A comprehensive ${planData.difficulty || "fitness"} level plan designed to help you achieve your ${planData.type || "fitness"} goals.`}
                </ReactMarkdown>
              </Box>
            </Grid>

            {/* Equipment */}
            {planData.equipment_needed &&
              planData.equipment_needed.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" component="h3" gutterBottom>
                    Equipment Needed
                  </Typography>
                  <Stack spacing={1}>
                    {planData.equipment_needed.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" component="span">
                          {item.name}
                        </Typography>
                        <Chip
                          label={`x${item.quantity}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              )}
          </Grid>
        </CardContent>
      </Card>

      {/* Getting Started Section */}
      {planData.getting_started && (
        <Card elevation={1} sx={{ mb: 3, bgcolor: "grey.50" }}>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              Getting Started
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {planData.getting_started}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TrainingPlanHeader;
